/**
 * The active profile's part of `localStorage` (docs/TZ-PROFILES.md 4.1): every key lives under
 * `vp:p:<profile id>:`, so profiles never read each other's chats, consent or view choices, and
 * deleting a profile removes exactly its keys. `profileLocal` has the `Storage` interface — code that
 * used `localStorage` switches to it unchanged.
 *
 * The active id is known synchronously (the registry mirrors it into `vp-active-profile`), so module
 * code that reads at import time already reads the right profile's keys.
 */
export const ACTIVE_KEY = 'vp-active-profile';
const PREFIX = 'vp:p:';

function readActive(): string | null {
  try {
    return localStorage.getItem(ACTIVE_KEY);
  } catch {
    return null;
  }
}

let scope: string | null = readActive();

/** The profile whose keys `profileLocal` reads (set when a profile is activated). */
export function setProfileScope(id: string | null): void {
  scope = id;
}

export function profileScope(): string | null {
  return scope;
}

const prefixOf = (id: string | null) => `${PREFIX}${id ?? '_'}:`;

/** The full `localStorage` key of a profile key. */
export function pkey(key: string, id: string | null = scope): string {
  return prefixOf(id) + key;
}

function ownKeys(id: string | null): string[] {
  const p = prefixOf(id);
  const out: string[] = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k?.startsWith(p)) out.push(k.slice(p.length));
    }
  } catch {
    /* storage blocked */
  }
  return out;
}

/** `Storage` over the active profile's keys (writes fail silently when the storage is full or blocked, like before). */
export const profileLocal: Storage = {
  get length() {
    return ownKeys(scope).length;
  },
  key(i: number): string | null {
    return ownKeys(scope)[i] ?? null;
  },
  getItem(key: string): string | null {
    try {
      return localStorage.getItem(pkey(key));
    } catch {
      return null;
    }
  },
  setItem(key: string, value: string): void {
    localStorage.setItem(pkey(key), value);
  },
  removeItem(key: string): void {
    try {
      localStorage.removeItem(pkey(key));
    } catch {
      /* storage blocked */
    }
  },
  clear(): void {
    clearProfileLocal(scope);
  },
};

/** Every key of a profile (without the prefix). */
export function profileLocalKeys(id: string): string[] {
  return ownKeys(id);
}

/** Remove every key of a profile (it is deleted). */
export function clearProfileLocal(id: string | null): void {
  for (const k of ownKeys(id)) {
    try {
      localStorage.removeItem(pkey(k, id));
    } catch {
      /* storage blocked */
    }
  }
}

/** Keys that belonged to nobody before profiles existed (moved into the first profile on update). */
export const LEGACY_KEYS = ['vp-legal-accepted', 'vp-canvas-opens', 'vp-home-layout', 'vp-palette-recent', 'vp.clip', 'vp.locale'];
export const LEGACY_PREFIXES = ['vp-chat-'];

/** Move the keys of the version without profiles under a profile; returns how many moved. */
export function adoptLegacyLocal(id: string, keys: readonly string[] = LEGACY_KEYS, prefixes: readonly string[] = LEGACY_PREFIXES): number {
  const found: string[] = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k || k.startsWith(PREFIX)) continue;
      if (keys.includes(k) || prefixes.some((p) => k.startsWith(p))) found.push(k);
    }
    for (const k of found) {
      const v = localStorage.getItem(k);
      if (v !== null) localStorage.setItem(pkey(k, id), v);
      localStorage.removeItem(k);
    }
  } catch {
    /* storage blocked: the keys stay where they were */
  }
  return found.length;
}

/** Export / import of a profile: all its keys and values. */
export function readProfileLocal(id: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const k of ownKeys(id)) {
    try {
      const v = localStorage.getItem(pkey(k, id));
      if (v !== null) out[k] = v;
    } catch {
      /* skip */
    }
  }
  return out;
}

export function writeProfileLocal(id: string, values: Record<string, string>): void {
  for (const [k, v] of Object.entries(values)) {
    try {
      localStorage.setItem(pkey(k, id), v);
    } catch {
      /* storage full: the rest still goes in */
    }
  }
}
